import { DropResult } from "react-beautiful-dnd";

/**
 * Where a pointer drag lands: the droppable under the pointer. The pure half of
 * `pointer-aimed-dnd.tsx`.
 *
 * react-beautiful-dnd picks a destination by box overlap, which suits one list and fails a stack of
 * them — a table's per-group `<tbody>`s, a tree's folders. Every droppable the lifted item's box
 * touches is a candidate, droppables of one width tie on its distance measure, and the tie goes to
 * whichever registered first: the one ABOVE. So a one-row droppable (an empty folder, its header
 * alone) under a neighbour could only be hit by overshooting it — aimed at its middle, the dragged
 * row still overlapped the neighbour by a pixel and the drop came back as that neighbour, usually
 * the list it was dragged from. rbd also lost every droppable of a list that sits in a sideways
 * scroller inside a scrolling page ("nested scroll container"). A list is aimed with the pointer,
 * so the pointer decides.
 */

/** A droppable's box, in viewport coordinates, as it stands now. */
export interface IDropTargetBox {
  id: string;
  top: number;
  bottom: number;
  left: number;
  right: number;
}

export interface IDropPoint {
  x: number;
  y: number;
}

const holds = (box: IDropTargetBox, point: IDropPoint): boolean =>
  point.x >= box.left && point.x < box.right && point.y >= box.top && point.y < box.bottom;

const areaOf = (box: IDropTargetBox): number => (box.right - box.left) * (box.bottom - box.top);

/**
 * The droppable whose box holds the point, or null over none of them (a heading, past the last row,
 * beside the list). Edges are half-open, so the edge two stacked droppables share belongs to the
 * one below; where droppables nest (a sub-folder inside its folder) the innermost — the smallest
 * box holding the point — is the one aimed at.
 */
export const findDropTargetAt = (boxes: readonly IDropTargetBox[], point: IDropPoint): string | null => {
  let best: IDropTargetBox | null = null;
  boxes.forEach((box: IDropTargetBox): void => {
    if (!holds(box, point)) return;
    if (best === null || areaOf(box) < areaOf(best)) best = box;
  });
  return best === null ? null : (best as IDropTargetBox).id;
};

/**
 * A pointer drop, re-aimed at the droppable under the pointer. rbd's result stands when it agrees —
 * its index too, so a reorder inside one list is untouched. Otherwise the destination is that
 * droppable (index 0: a list rbd did not recognise has no position to report), the source's own
 * slot when the pointer is back over it (a drop back, which moves nothing), or none when the pointer
 * is over no droppable — let go clear of the list. A cancelled drag is left alone.
 */
export const aimDropAtPointer = (result: DropResult, pointerDroppableId: string | null): DropResult => {
  if (result.reason === "CANCEL") return result;
  if (pointerDroppableId === null) return { ...result, destination: null };
  if (result.destination?.droppableId === pointerDroppableId) return result;
  if (pointerDroppableId === result.source.droppableId) {
    return { ...result, destination: { droppableId: pointerDroppableId, index: result.source.index } };
  }
  return { ...result, destination: { droppableId: pointerDroppableId, index: 0 } };
};
